import { Icon, Spinner, ThemeColor } from "@deligoo/ui"
import { IconName } from "@deligoo/ui/src/icons/build"
import clsx from "clsx"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"

import { Team, TeamStats } from "@/types"
import { getStatsColor, getTrafficValueColor, getWeatherIconName } from "@/utils"

import { EventsTableHeader } from "../EventsList"
import { changeTeam, handleDriversCountClick, handleOrdersCountClick, handleUnsettledPaymentsClick } from "../utils"
import cls from "./EventsStatistics.module.scss"

export type TeamWithStats = {
  team: Team
  stats?: TeamStats
}

type TeamsStatsProps = {
  teamWithStats: TeamWithStats
  hiddenColumns: EventsTableHeader[]
}

export const TeamsStats = ({ teamWithStats, hiddenColumns }: TeamsStatsProps) => {
  const team = teamWithStats.team
  const stats = teamWithStats.stats

  const history = useHistory()

  const teamStatsStatus = !stats ? "loading" : "fetched"

  if (!stats) {
    return (
      <div className={cls.teamStats}>
        <div className={cls.teamHeader}>
          <h6 className={cls.teamName} onClick={() => changeTeam(team, history)}>
            {team.name}
          </h6>
        </div>
        {teamStatsStatus === "loading" && (
          <div className={cls.loading}>
            <Spinner />
          </div>
        )}
      </div>
    )
  }

  const {
    total_drivers_count,
    active_drivers_count,
    unsettled_payments_count,
    today_orders_count,
    today_delivery_orders_count,
    load_value,
    blocked_drivers_count,
    late_drivers_count,
    dynamic_buffer_real_value,
    traffic_value,
    omitted_orders_count,
    weather,
  } = stats

  return (
    <div className={clsx(cls.teamStats, getStatsColor(stats) === "alert" && cls.alertBorder)}>
      <div className={cls.teamHeader}>
        <h6 className={cls.teamName} onClick={() => changeTeam(team, history)}>
          {team.name}
        </h6>

        {teamStatsStatus === "fetched" && <div className={clsx(cls.teamState, `text-${getStatsColor(stats)}`)} />}
      </div>
      {teamStatsStatus === "loading" ? (
        <div className={cls.loading}>
          <Spinner />
        </div>
      ) : (
        <>
          {!hiddenColumns.includes("active_drivers_count") && (
            <TeamStatItem
              label="active_drivers_count"
              value={active_drivers_count}
              valueColor={getStatsColor(stats, "active_drivers_count")}
              valueTotal={total_drivers_count}
              onMouseDown={(event) => handleDriversCountClick("inactive", team, event, history)}
            />
          )}

          {!hiddenColumns.includes("blocked_drivers_count") && (
            <TeamStatItem
              label="blocked_drivers_count"
              value={blocked_drivers_count}
              valueColor={getStatsColor(stats, "blocked_drivers_count")}
              valueTotal={total_drivers_count}
              onMouseDown={(event) => handleDriversCountClick("blocked", team, event, history)}
            />
          )}

          {!(hiddenColumns.includes("load_value") || hiddenColumns.includes("today_orders_count")) && (
            <TeamStatItem
              label="load_value"
              value={
                <>
                  <span className={`text-${getStatsColor(stats, "today_orders_count")}`}>{today_orders_count}</span>
                  <span className={cls.separator} style={{ margin: "0 .5rem" }}>
                    /
                  </span>
                  <span className={`text-${getStatsColor(stats, "load_value")}`}>{load_value?.toFixed(2)}</span>
                </>
              }
            />
          )}

          {!hiddenColumns.includes("late_drivers_count") && (
            <TeamStatItem
              label="late_drivers_count"
              value={late_drivers_count}
              valueColor={getStatsColor(stats, "late_drivers_count")}
              valueTotal={total_drivers_count}
              onMouseDown={(event) => handleDriversCountClick("late", team, event, history)}
            />
          )}

          {!hiddenColumns.includes("today_delivery_orders_count") && (
            <TeamStatItem
              label="today_delivery_orders_count"
              value={today_delivery_orders_count}
              valueColor={getStatsColor(stats, "today_delivery_orders_count")}
            />
          )}

          {!hiddenColumns.includes("unsettled_payments_count") && (
            <TeamStatItem
              label="unsettled_payments_count"
              value={unsettled_payments_count}
              valueColor={getStatsColor(stats, "unsettled_payments_count")}
              onMouseDown={(event) => handleUnsettledPaymentsClick(team, event, history)}
            />
          )}

          {!hiddenColumns.includes("dynamic_buffer_real_value") && (
            <TeamStatItem
              label="dynamic_buffer_real_value"
              value={dynamic_buffer_real_value ? `${dynamic_buffer_real_value} min.` : dynamic_buffer_real_value}
              valueColor={getStatsColor(stats, "dynamic_buffer_real_value")}
            />
          )}

          {!hiddenColumns.includes("traffic_value") && (
            <TeamStatItem
              label="traffic_value"
              value={traffic_value ? `${traffic_value}%` : "-"}
              valueColor={getTrafficValueColor(traffic_value)}
            />
          )}

          {!hiddenColumns.includes("omitted_orders_count") && (
            <TeamStatItem
              label="omitted_orders_count"
              value={omitted_orders_count}
              valueColor={getStatsColor(stats, "omitted_orders_count")}
              onMouseDown={(event) => handleOrdersCountClick("omitted", team, event, history)}
            />
          )}

          {!hiddenColumns.includes("weather") && (
            <TeamStatItem
              label="weather"
              value={weather ? <span>{Math.round(weather.temperature)}℃</span> : "-"}
              iconName={weather ? getWeatherIconName(weather.main) : "help"}
            />
          )}
        </>
      )}
    </div>
  )
}

type TeamStatItemProps = {
  label: keyof TeamStats
  value: React.ReactNode
  valueTotal?: number
  valueColor?: ThemeColor
  iconName?: IconName
  onMouseDown?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}

const TeamStatItem = ({ label, value, valueColor, valueTotal, iconName, onMouseDown }: TeamStatItemProps) => {
  const { t } = useTranslation()

  return (
    <div className={clsx(cls.teamStatItem, onMouseDown && "cursor-pointer")} onMouseDown={onMouseDown}>
      <Icon name={iconName ? iconName : getStatItemIcon(label)} className={cls.icon} />

      <div className={clsx(cls.label, "truncate")}>{t(`stats.fullscreen.${label}`)}</div>

      <div className={cls.value}>
        <span className={clsx(valueColor && `text-${valueColor}`)}>{value}</span>
        {typeof valueTotal === "number" ? <span className={cls.valueTotal}>{valueTotal}</span> : null}
      </div>
    </div>
  )
}

function getStatItemIcon(label: keyof TeamStats): IconName {
  switch (label) {
    case "active_drivers_count":
      return "courier"
    case "unsettled_payments_count":
      return "no-credit-card"
    case "today_delivery_orders_count":
    case "omitted_orders_count":
      return "delivery"
    case "load_value":
      return "speedometer"
    case "blocked_drivers_count":
      return "courier"
    case "traffic_value":
      return "traffics"
    case "late_drivers_count":
      return "courier"
    case "dynamic_buffer_real_value":
      return "pickup"
    default:
      return "boxes"
  }
}
